import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { SettingsPage } from '../components/SettingsPage';
import { MessageAside } from '../components/SettingsAsides';
import { useParams } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { PageHeader, SavedNote, SidePanel, Switch, TextArea } from '../components/forms';
import { Button, ErrorNote } from '../components/ui';
import { api, ApiError, type Salon } from '../lib/api';
import { useLocale, useT, type TKey } from '../lib/i18n';

type Kind = 'confirmation' | 'reminder' | 'changed' | 'cancelled' | 'team_booked' | 'team_cancelled';
type Settings = {
  confirmation: { enabled: boolean; extra: string | null };
  reminder: { enabled: boolean; hoursBefore: 2 | 4 | 12 | 24 | 48; extra: string | null };
  changed: { enabled: boolean; extra: string | null };
  cancelled: { enabled: boolean; extra: string | null };
  team_booked: { enabled: boolean };
  team_cancelled: { enabled: boolean };
  whatsapp?: { enabled: boolean };
  review?: { enabled: boolean; url: string | null; delayHours: 3 | 24 };
  /** Only true once the server has a WhatsApp provider; until then the switch stays hidden. */
  whatsappAvailable?: boolean;
};
const clientKinds = ['confirmation', 'reminder', 'changed', 'cancelled'] as const;
type ClientKind = (typeof clientKinds)[number];

const groups: Array<{ title: TKey; kinds: Kind[] }> = [
  { title: 'msg.group.booking', kinds: ['confirmation'] },
  { title: 'msg.group.before', kinds: ['reminder'] },
  { title: 'msg.group.change', kinds: ['changed', 'cancelled'] },
  { title: 'msg.group.team', kinds: ['team_booked', 'team_cancelled'] },
];

/** Salon → Berichten: which emails go out, when, and the salon's own words (plan 2.4). */
export function MessagesPage() {
  const { orgId } = useParams({ from: '/s/$orgId' });
  const t = useT();
  const qc = useQueryClient();
  const key = ['message-settings', orgId];
  const settings = useQuery({ queryKey: key, queryFn: () => api<Settings>(`/orgs/${orgId}/message-settings`) });
  const salon = useQuery({ queryKey: ['salon', orgId], queryFn: () => api<Salon>(`/orgs/${orgId}`) });
  const [saved, setSaved] = useState(false);
  const [editing, setEditing] = useState<ClientKind | null>(null);
  const save = useMutation({
    mutationFn: (s: Settings) => api<Settings>(`/orgs/${orgId}/message-settings`, { method: 'PUT', body: JSON.stringify(s) }),
    onMutate: (s) => qc.setQueryData(key, s),
    onSuccess: (s) => {
      qc.setQueryData(key, s);
      setSaved(true);
      window.setTimeout(() => setSaved(false), 1800);
    },
    onError: () => void qc.invalidateQueries({ queryKey: key }),
  });
  const s = settings.data;
  const manager = salon.data?.role !== 'staff';
  if (!s) return <p className="text-muted">{t('common.loading')}</p>;
  const set = <K extends Kind>(kind: K, patch: Partial<Settings[K]>) => save.mutate({ ...s, [kind]: { ...s[kind], ...patch } });

  return (
    <SettingsPage orgId={orgId} aside={<MessageAside orgId={orgId} settings={s} />}>
      <PageHeader orgId={orgId} title={t('msg.title')} intro={t('msg.intro')} action={<SavedNote show={saved} />} />
      <ErrorNote message={save.error instanceof ApiError ? save.error.message : save.error ? t('error.generic') : null} />
      {s.whatsappAvailable && (
        <div className="rounded-[var(--radius-card)] border border-line bg-surface px-4 py-4">
          <Switch label={t('msg.whatsapp')} hint={t('msg.whatsapp.hint')} checked={!!s.whatsapp?.enabled} onChange={(v) => save.mutate({ ...s, whatsapp: { enabled: v } })} />
        </div>
      )}
      {groups.map((g) => (
        <section key={g.title} className="flex flex-col gap-2">
          <h2 className="eyebrow">{t(g.title)}</h2>
          <ul className="divide-y divide-line overflow-hidden rounded-[var(--radius-card)] border border-line bg-surface">
            {g.kinds.map((kind) => {
              const isClient = (clientKinds as readonly string[]).includes(kind);
              const extra = isClient ? s[kind as ClientKind].extra : null;
              return (
                <li key={kind} className="flex flex-col gap-3 px-4 py-4">
                  <Switch
                    label={t(`msg.${kind}` as TKey)}
                    hint={t(`msg.${kind}.hint` as TKey)}
                    checked={s[kind].enabled}
                    onChange={(enabled) => manager && set(kind, { enabled } as Partial<Settings[typeof kind]>)}
                  />
                  {s[kind].enabled && (
                    <div className="flex flex-wrap items-center gap-2 text-sm">
                      <span className="rounded-full bg-subtle px-2.5 py-0.5 font-medium text-muted">{t('msg.email')}</span>
                      {kind === 'reminder' && (
                        <select
                          aria-label={t('msg.timing')}
                          disabled={!manager}
                          value={s.reminder.hoursBefore}
                          onChange={(e) => set('reminder', { hoursBefore: Number(e.target.value) as Settings['reminder']['hoursBefore'] })}
                          className="min-h-9 rounded-[var(--radius-control)] border border-line bg-surface px-2"
                        >
                          {[2, 4, 12, 24, 48].map((h) => (
                            <option key={h} value={h}>
                              {t('msg.hoursBefore', { n: h })}
                            </option>
                          ))}
                        </select>
                      )}
                      {isClient && manager && (
                        <button type="button" className="font-semibold text-accent-strong hover:underline" onClick={() => setEditing(kind as ClientKind)}>
                          {extra ? t('msg.editText') : t('msg.addText')}
                        </button>
                      )}
                      {extra && <span className="min-w-0 flex-1 truncate text-muted">“{extra}”</span>}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </section>
      ))}
      {s.review && <ReviewSection review={s.review} manager={manager} onSave={(review) => save.mutate({ ...s, review })} />}
      <p className="text-sm text-muted">{t('msg.later')}</p>
      {editing && <TextEditor orgId={orgId} kind={editing} settings={s} onClose={() => setEditing(null)} onSave={(next) => save.mutate(next, { onSuccess: () => setEditing(null) })} />}
    </SettingsPage>
  );
}

/** Own text with a live preview of the real email, and "Stuur mij een test". */
function TextEditor({ orgId, kind, settings, onClose, onSave }: { orgId: string; kind: ClientKind; settings: Settings; onClose: () => void; onSave: (s: Settings) => void }) {
  const t = useT();
  const locale = useLocale();
  const [text, setText] = useState(settings[kind].extra ?? '');
  const draft: Settings = { ...settings, [kind]: { ...settings[kind], extra: text.trim() || null } };
  const [debounced, setDebounced] = useState(draft);
  useEffect(() => {
    const id = window.setTimeout(() => setDebounced(draft), 300);
    return () => window.clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text]);
  const preview = useQuery({
    queryKey: ['message-preview', orgId, kind, debounced[kind].extra, locale],
    queryFn: () => api<{ subject: string; html: string }>(`/orgs/${orgId}/message-settings/preview`, { method: 'POST', body: JSON.stringify({ kind, settings: debounced, locale }) }),
    placeholderData: (p) => p,
  });
  const test = useMutation({ mutationFn: () => api<{ to: string }>(`/orgs/${orgId}/message-settings/test`, { method: 'POST', body: JSON.stringify({ kind, settings: draft }) }) });

  return (
    <SidePanel
      open
      title={t(`msg.${kind}` as TKey)}
      onClose={onClose}
      footer={
        <>
          <Button onClick={() => onSave(draft)}>{t('common.save')}</Button>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <TextArea id="msg-extra" label={t('msg.ownText')} placeholder={t('msg.ownTextPlaceholder')} maxLength={500} value={text} onChange={(e) => setText(e.target.value)} />
        <p className="-mt-2 text-sm text-muted">{t('msg.ownTextHint')}</p>
        <div className="flex flex-col gap-2">
          <p className="text-sm font-semibold">{t('msg.preview')}</p>
          {preview.data && <p className="text-sm text-muted">{preview.data.subject}</p>}
          <iframe title={t('msg.preview')} sandbox="allow-same-origin" srcDoc={preview.data?.html ?? ''} className="h-[520px] w-full rounded-[var(--radius-control)] border border-line bg-canvas" />
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Button variant="secondary" onClick={() => test.mutate()} disabled={test.isPending}>
            {t('msg.sendTest')}
          </Button>
          {test.data && <span className="text-sm text-accent-strong">✓ {t('msg.testSent', { email: test.data.to })}</span>}
        </div>
        <ErrorNote message={test.error instanceof ApiError ? test.error.message : null} />
      </div>
    </SidePanel>
  );
}

/** "Na het bezoek": the review request, off by default, with the salon's own review link. */
function ReviewSection({ review, manager, onSave }: { review: NonNullable<Settings['review']>; manager: boolean; onSave: (r: NonNullable<Settings['review']>) => void }) {
  const t = useT();
  const [url, setUrl] = useState(review.url ?? '');
  const [error, setError] = useState<string | null>(null);
  const valid = /^https:\/\/\S+\.\S+/.test(url.trim());
  const save = (patch: Partial<NonNullable<Settings['review']>>) => {
    const next = { ...review, url: url.trim() || null, ...patch };
    if (next.enabled && !(next.url && /^https:\/\/\S+\.\S+/.test(next.url))) return setError(t('msg.review.needUrl'));
    setError(null);
    onSave(next);
  };
  return (
    <section className="flex flex-col gap-2">
      <h2 className="eyebrow">{t('msg.group.after')}</h2>
      <div className="flex flex-col gap-4 rounded-[var(--radius-card)] border border-line bg-surface px-4 py-4">
        <Switch label={t('msg.review')} hint={t('msg.review.hint')} checked={review.enabled} onChange={(v) => manager && save({ enabled: v })} />
        <label className="flex flex-col gap-1.5">
          <span className="text-sm font-medium">{t('msg.review.url')}</span>
          <input
            type="url"
            inputMode="url"
            placeholder="https://g.page/r/…/review"
            value={url}
            disabled={!manager}
            onChange={(e) => setUrl(e.target.value)}
            onBlur={() => url.trim() !== (review.url ?? '') && (valid || !url.trim()) && save({})}
            className="min-h-11 rounded-[var(--radius-control)] border border-line bg-surface px-3"
          />
          <span className="text-sm text-muted">{t('msg.review.urlHint')}</span>
        </label>
        <label className="flex flex-wrap items-center gap-2 text-sm">
          <span className="font-medium">{t('msg.review.when')}</span>
          <select value={review.delayHours} disabled={!manager} onChange={(e) => save({ delayHours: Number(e.target.value) as 3 | 24 })} className="min-h-9 rounded-[var(--radius-control)] border border-line bg-surface px-2">
            <option value={3}>{t('msg.review.after3')}</option>
            <option value={24}>{t('msg.review.after24')}</option>
          </select>
        </label>
        <ErrorNote message={error} />
      </div>
    </section>
  );
}
